import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { api } from "../../api";
import { CollectionLanguage, GridRow, Image, PlainDetails } from "../../../types/admin";
import { ModalState } from "../../../types/modals";
import { getSelectOptions, toastSuccess } from "../admin";
import { deleteResult, updateResults } from "../adminList";

export type CollectionEditModalState = ModalState & {
    languageTab: string;
    collection_id: string | null;
    languages: CollectionLanguage[];
    teams: PlainDetails[];
    team_options: PlainDetails[];
    games: PlainDetails[];
    slide_rows: GridRow[];
    slide_id: string | null;
    lift_image: Image | null;
    isUploading: boolean;
};

const initialState: CollectionEditModalState = {
    loading: false,
    isOpen: false,
    error: null,
    languageTab: '',
    collection_id: null,
    languages: [],
    teams: [],
    team_options: [],
    games: [],
    slide_rows: [],
    slide_id: null,
    lift_image: null,
    isUploading: false,
};

type CollectionModalPayload = {
    collection_id: string | null;
    language: string;
};

export type NewCollectionPayload = {
    languages: CollectionLanguage[];
    teams: PlainDetails[];
    games: PlainDetails[];
};

export type UpdateCollectionPayload = NewCollectionPayload & {
    collection_id: string;
};

export const getCollection = createAsyncThunk(
    'collectionEditModal/getCollection',
    async (payload: { collection_id: string }, thunkAPI) => {
        const { collection_id } = payload;
        try {
            return await api.get(`/API/admin/collections/${collection_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveNewCollection = createAsyncThunk(
    'collectionEditModal/saveNewCollection',
    async (payload: NewCollectionPayload, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.post(`/API/admin/collections/`, payload);
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const saveCollection = createAsyncThunk(
    'collectionEditModal/saveCollection',
    async (payload: UpdateCollectionPayload, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        try {
            const response = await api.patch(`/API/admin/collections/${payload.collection_id}/`, payload);
            dispatch(updateResults({ result: response }));
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const uploadCollectionLiftImage = createAsyncThunk(
    'collectionEditModal/uploadCollectionLiftImage',
    async (payload: FormData, thunkAPI) => {
        const collection_id = payload.get('collection_id');
        if (collection_id === null) {
            return thunkAPI.rejectWithValue('No collection id');
        }
        try {
            return await api.post(`/API/admin/collections/${collection_id}/lift-image/`, payload, true);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const deleteCollection = createAsyncThunk(
    'collectionEditModal/deleteCollection',
    async (collection_id: string, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.delete(`/API/admin/collections/${collection_id}/`);
            if (response.key) {
                dispatch(deleteResult({ ...response }));
            }
            if (response.success_message) {
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type SlideRowsPayload = {
    slide_id: string;
    slide_rows: GridRow[];
};

export const saveCollectionSlideRows = createAsyncThunk(
    'collectionEditModal/saveCollectionSlideRows',
    async (payload: SlideRowsPayload, thunkAPI) => {
        const { slide_id } = payload;
        try {
            const response = await api.patch(`/API/admin/collection-slides/${slide_id}/rows/`, payload);
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type DeleteRowPayload = {
    slide_id: string;
    row_id: string;
};

export const deleteCollectionRow = createAsyncThunk(
    'collectionEditModal/deleteCollectionRow',
    async (payload: DeleteRowPayload, thunkAPI) => {
        const { slide_id, row_id } = payload;
        try {
            const response = await api.delete(`/API/admin/collection-slides/${slide_id}/rows/${row_id}/`);
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type DeleteElementPayload = {
    slide_id: string;
    element_id: string;
};

export const deleteCollectionElement = createAsyncThunk(
    'collectionEditModal/deleteCollectionElement',
    async (payload: DeleteElementPayload, thunkAPI) => {
        const { slide_id, element_id } = payload;
        try {
            const response = await api.delete(`/API/admin/collection-slides/${slide_id}/elements/${element_id}/`);
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

const slice = createSlice({
    name: 'collectionEditModal',
    initialState,
    reducers: {
        openModal: (state, action: PayloadAction<CollectionModalPayload>) => {
            state.collection_id = action.payload.collection_id;
            state.languageTab = action.payload.language;
            state.isOpen = true;
        },
        setActiveLanguage: (state, action: PayloadAction<string>) => {
            state.languageTab = action.payload;
        },
        closeModal: () => {
            return initialState;
        },
        changeCollectionModal: (state, action: PayloadAction<Partial<CollectionEditModalState>>) => {
            return {
                ...state,
                ...action.payload
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCollection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCollection.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                Object.assign(state, action.payload);
            })
            .addCase(getSelectOptions.fulfilled, (state, action) => {
                if (action.payload.team_options) state.team_options = action.payload.team_options;
            })
            .addCase(getCollection.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveNewCollection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveNewCollection.fulfilled, (state, action) => {
                return initialState;
            })
            .addCase(saveNewCollection.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveCollection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveCollection.fulfilled, () => {
                return initialState;
            })
            .addCase(saveCollection.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(uploadCollectionLiftImage.pending, (state) => {
                state.isUploading = true;
            })
            .addCase(uploadCollectionLiftImage.fulfilled, (state, action) => {
                state.isUploading = false;
                state.lift_image = action.payload.lift_image;
            })
            .addCase(uploadCollectionLiftImage.rejected, (state, action) => {
                state.isUploading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteCollection.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteCollection.fulfilled, () => {
                return initialState;
            })
            .addCase(deleteCollection.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(saveCollectionSlideRows.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveCollectionSlideRows.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.slide_rows = action.payload.slide_rows;
            })
            .addCase(saveCollectionSlideRows.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteCollectionRow.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteCollectionRow.fulfilled, (state, action) => {
                state.loading = false;
                state.slide_rows = action.payload.slide_rows;
            })
            .addCase(deleteCollectionRow.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(deleteCollectionElement.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteCollectionElement.fulfilled, (state, action) => {
                state.loading = false;
                state.slide_rows = action.payload.slide_rows;
            })
            .addCase(deleteCollectionElement.rejected, (state, action) => {
                state.loading = false;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            });
    }
});

export const {
    openModal,
    closeModal,
    changeCollectionModal,
    setActiveLanguage,
} = slice.actions;

export default slice.reducer;
